import { useState, type FormEvent } from 'react';
import { BUTTON_PRIMARY_LG, FIELD_LABEL, FIELD_WIDE } from '../controlStyles';

/** Personal mail providers: their domain names the provider, not the user's company. */
const PERSONAL_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'ymail.com',
  'icloud.com', 'me.com', 'mac.com', 'proton.me', 'protonmail.com', 'pm.me', 'aol.com', 'gmx.com', 'gmx.net',
  'fastmail.com', 'hey.com', 'zoho.com', 'yandex.com', 'yandex.ru', 'mail.com', 'qq.com', '163.com',
]);

/** Second-level labels under a country code (`acme.co.uk`), skipped when naming from a domain. */
const GENERIC_SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'ac', 'gov', 'edu']);

function titleWords(text: string): string {
  return text
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * A starting team name from the signed-in email: the company from a work
 * domain (`ada@acme-robotics.io` -> "Acme Robotics"), otherwise the person
 * (`ada.lovelace@gmail.com` -> "Ada's team"). Only a suggestion; the user edits it.
 */
export function suggestTeamName(email: string): string {
  const [local = '', domain = ''] = email.trim().toLowerCase().split('@');
  const labels = domain.split('.').filter(Boolean);
  if (labels.length >= 2 && !PERSONAL_MAIL_DOMAINS.has(domain)) {
    let index = labels.length - 2;
    if (index > 0 && GENERIC_SECOND_LEVEL.has(labels[index]!)) index -= 1;
    const company = titleWords(labels[index]!);
    if (company) return company;
  }
  const person = titleWords(local.split('+')[0]!.split(/[._-]/)[0] ?? '');
  return person ? `${person}'s team` : '';
}

/**
 * The connect page's step for a user with no team: name one and create it on
 * the way to Allow. Allowing without a team is the page's other action, for
 * people who would rather let the connecting app create it.
 */
export function NoTeamStep({
  email,
  busy,
  disabled,
  onCreate,
}: {
  email: string;
  busy: boolean;
  disabled: boolean;
  onCreate: (name: string) => void;
}) {
  const [name, setName] = useState(() => suggestTeamName(email));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) onCreate(trimmed);
  };
  return (
    <form className="connect-no-team grid gap-3" onSubmit={submit}>
      <p className="m-0 text-[0.88rem] leading-relaxed text-nim-muted">
        You are not in a team yet. Create one now, or allow without a team and create it from the app.
      </p>
      <label className="grid gap-1.5">
        <span className={FIELD_LABEL}>Team name</span>
        <input
          className={FIELD_WIDE}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Acme"
          maxLength={120}
          disabled={busy}
        />
      </label>
      <button className={`${BUTTON_PRIMARY_LG} justify-self-start`} type="submit" disabled={busy || disabled || !name.trim()}>
        {busy ? 'Working…' : 'Create team and allow'}
      </button>
    </form>
  );
}
